import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './WebhookConfigsViewer.css';

// Opciones de sources disponibles para mapeo de variables
const SOURCE_OPTIONS = [
  { group: 'Deal', options: [
    { value: 'deal.pipedriveId', label: 'Deal ID' },
    { value: 'deal.title', label: 'Deal Title' },
    { value: 'deal.value', label: 'Deal Value' },
    { value: 'deal.currency', label: 'Deal Currency' },
    { value: 'deal.status', label: 'Deal Status' },
    { value: 'deal.customFields', label: 'Deal Custom Fields (objeto)' },
  ]},
  { group: 'Person', options: [
    { value: 'person.pipedriveId', label: 'Person ID' },
    { value: 'person.name', label: 'Person Name' },
    { value: 'person.firstName', label: 'Person First Name' },
    { value: 'person.lastName', label: 'Person Last Name' },
    { value: 'person.orgName', label: 'Person Org Name' },
    { value: 'person.customFields', label: 'Person Custom Fields (objeto)' },
  ]},
  { group: 'Activity', options: [
    { value: 'activity.pipedriveId', label: 'Activity ID' },
    { value: 'activity.subject', label: 'Activity Subject' },
    { value: 'activity.type', label: 'Activity Type' },
    { value: 'activity.note', label: 'Activity Note' },
    { value: 'activity.dueDate', label: 'Activity Due Date' },
    { value: 'activity.done', label: 'Activity Done' },
  ]},
];

function WebhookConfigsViewer() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    reportType: '',
    isActive: true,
    requiresAuth: false,
    variableMappings: [],
  });

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/webhook-configs');
      setConfigs(response.data);
      setError(null);
    } catch (err) {
      setError('Error cargando configuraciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      reportType: '',
      isActive: true,
      requiresAuth: false,
      variableMappings: [],
    });
    setShowModal(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      description: config.description || '',
      reportType: config.reportType,
      isActive: config.isActive,
      requiresAuth: config.requiresAuth,
      variableMappings: config.variableMappings || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta configuración?')) return;

    try {
      await api.delete(`/api/webhook-configs/${id}`);
      loadConfigs();
    } catch (err) {
      setError('Error eliminando configuración');
      console.error(err);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.post(`/api/webhook-configs/${id}/toggle`);
      loadConfigs();
    } catch (err) {
      setError('Error cambiando estado');
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingConfig) {
        await api.patch(`/api/webhook-configs/${editingConfig._id}`, formData);
      } else {
        await api.post('/api/webhook-configs', formData);
      }
      setShowModal(false);
      loadConfigs();
    } catch (err) {
      setError(err.response?.data?.message || 'Error guardando configuración');
      console.error(err);
    }
  };

  const addMapping = () => {
    setFormData({
      ...formData,
      variableMappings: [...formData.variableMappings, { key: '', source: '' }],
    });
  };

  const removeMapping = (index) => {
    const newMappings = [...formData.variableMappings];
    newMappings.splice(index, 1);
    setFormData({ ...formData, variableMappings: newMappings });
  };

  const updateMapping = (index, field, value) => {
    const newMappings = [...formData.variableMappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setFormData({ ...formData, variableMappings: newMappings });
  };

  const getWebhookUrl = (name) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3320';
    return `${baseUrl}/api/notification-webhooks/${name}`;
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="webhook-configs-viewer">
      <div className="header">
        <h2>
          <span className="icon">🔗</span>
          Configuración de Webhooks para Notificaciones
        </h2>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Nuevo Webhook
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="configs-list">
        {configs.length === 0 ? (
          <div className="empty-state">
            <p>No hay webhooks configurados</p>
            <p>Crea uno nuevo para comenzar a recibir notificaciones de Pipedrive</p>
          </div>
        ) : (
          configs.map((config) => (
            <div key={config._id} className={`config-card ${!config.isActive ? 'inactive' : ''}`}>
              <div className="config-header">
                <div className="config-name">
                  <h3>{config.name}</h3>
                  <span className={`badge ${config.isActive ? 'active' : 'inactive'}`}>
                    {config.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                  {config.requiresAuth && (
                    <span className="badge auth">Auth</span>
                  )}
                </div>
                <div className="config-actions">
                  <button className="btn btn-sm" onClick={() => handleToggle(config._id)}>
                    {config.isActive ? 'Desactivar' : 'Activar'}
                  </button>
                  <button className="btn btn-sm" onClick={() => handleEdit(config)}>
                    Editar
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(config._id)}>
                    Eliminar
                  </button>
                </div>
              </div>

              <div className="config-details">
                <div className="detail-row">
                  <strong>URL del Webhook:</strong>
                  <code className="webhook-url">{getWebhookUrl(config.name)}</code>
                </div>
                <div className="detail-row">
                  <strong>Tipo de Reporte:</strong>
                  <span>{config.reportType}</span>
                </div>
                {config.description && (
                  <div className="detail-row">
                    <strong>Descripción:</strong>
                    <span>{config.description}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Mappings ({config.variableMappings?.length || 0}):</strong>
                  {config.variableMappings?.length > 0 && (
                    <div className="mappings-preview">
                      {config.variableMappings.map((m, i) => (
                        <span key={i} className="mapping-tag">
                          {m.key} ← {m.source}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal para crear/editar */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingConfig ? 'Editar Webhook' : 'Nuevo Webhook'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre (usado en la URL)</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                  placeholder="mi_webhook"
                  required
                  disabled={!!editingConfig}
                />
                <small>Solo letras minúsculas, números, guiones y guiones bajos</small>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del webhook"
                />
              </div>

              <div className="form-group">
                <label>Tipo de Reporte</label>
                <input
                  type="text"
                  value={formData.reportType}
                  onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                  placeholder="actividad_pipedrive"
                  required
                />
                <small>Este será el "type" del ReportItem en notificaciones</small>
              </div>

              <div className="form-row">
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Activo
                  </label>
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.requiresAuth}
                      onChange={(e) => setFormData({ ...formData, requiresAuth: e.target.checked })}
                    />
                    Requiere Basic Auth
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>
                  Mapeo de Variables
                  <button type="button" className="btn btn-sm" onClick={addMapping}>
                    + Agregar
                  </button>
                </label>

                <div className="mappings-editor">
                  {formData.variableMappings.map((mapping, index) => (
                    <div key={index} className="mapping-row">
                      <input
                        type="text"
                        placeholder="nombre_variable"
                        value={mapping.key}
                        onChange={(e) => updateMapping(index, 'key', e.target.value)}
                      />
                      <span className="arrow">←</span>
                      <input
                        type="text"
                        placeholder="deal.title o person.name"
                        value={mapping.source}
                        onChange={(e) => updateMapping(index, 'source', e.target.value)}
                        list={`sources-${index}`}
                      />
                      <datalist id={`sources-${index}`}>
                        {SOURCE_OPTIONS.map((group) => (
                          <optgroup key={group.group} label={group.group}>
                            {group.options.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </datalist>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeMapping(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {formData.variableMappings.length === 0 && (
                    <p className="hint">
                      Agrega mappings para extraer datos de deal, person o activity.
                      <br />
                      Ejemplo: nombre_completo ← person.name
                    </p>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingConfig ? 'Guardar Cambios' : 'Crear Webhook'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebhookConfigsViewer;
